
import React, { useState, useEffect } from 'react';
import { formatEther } from '../../services/web3';

interface DisputeResolutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (clientPercent: number, providerPercent: number) => Promise<void>;
  milestoneId: number;
  milestoneDescription: string;
  milestoneAmount: string; // in wei
  loading?: boolean;
}

const DisputeResolutionModal: React.FC<DisputeResolutionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  milestoneId,
  milestoneDescription,
  milestoneAmount,
  loading = false,
}) => {
  const [clientPercent, setClientPercent] = useState(50);
  const [providerPercent, setProviderPercent] = useState(50);
  const [error, setError] = useState<string | null>(null);

  const totalAmount = parseFloat(formatEther(milestoneAmount));
  const clientAmount = (totalAmount * clientPercent) / 100;
  const providerAmount = (totalAmount * providerPercent) / 100;

  // Auto-adjust provider percentage when client changes
  const handleClientChange = (value: number) => {
    const clamped = Math.max(0, Math.min(100, value));
    setClientPercent(clamped);
    setProviderPercent(100 - clamped);
    setError(null);
  };

  // Auto-adjust client percentage when provider changes
  const handleProviderChange = (value: number) => {
    const clamped = Math.max(0, Math.min(100, value));
    setProviderPercent(clamped);
    setClientPercent(100 - clamped);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate totals 100%
    if (clientPercent + providerPercent !== 100) {
      setError('Client and provider percentages must total 100%');
      return;
    }

    onSubmit(clientPercent, providerPercent);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-semibold text-[#471b00] font-poly">
              Resolve Dispute
            </h3>
            <p className="text-sm text-[#471b007f] font-dm mt-1">
              Milestone {milestoneId + 1}: {milestoneDescription}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-[#471b007f] hover:text-[#471b00] transition-colors disabled:opacity-50"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex gap-2">
            <svg
              className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="text-sm text-blue-800 font-dm">
              <p className="font-medium mb-1">Fund Distribution:</p>
              <p className="text-xs">
                Specify how the milestone funds ({totalAmount.toFixed(4)} MATIC) should be split between client and provider. Percentages must total 100%.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 mb-4">
            {/* Client Percentage */}
            <div>
              <label
                htmlFor="clientPercent"
                className="block text-sm font-medium text-[#471b00] mb-2 font-dm"
              >
                Client Share <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3 items-center">
                <input
                  type="number"
                  id="clientPercent"
                  value={clientPercent}
                  onChange={(e) => handleClientChange(parseInt(e.target.value) || 0)}
                  disabled={loading}
                  min="0"
                  max="100"
                  className="flex-1 px-4 py-2 border border-[#471b001e] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF3333] focus:border-transparent font-dm text-sm disabled:opacity-50"
                />
                <span className="text-sm font-medium text-[#471b00] font-dm w-16">
                  {clientPercent}%
                </span>
              </div>
              <p className="text-xs text-[#471b007f] mt-1 font-dm">
                Client receives: {clientAmount.toFixed(4)} MATIC
              </p>
            </div>

            {/* Provider Percentage */}
            <div>
              <label
                htmlFor="providerPercent"
                className="block text-sm font-medium text-[#471b00] mb-2 font-dm"
              >
                Provider Share <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3 items-center">
                <input
                  type="number"
                  id="providerPercent"
                  value={providerPercent}
                  onChange={(e) => handleProviderChange(parseInt(e.target.value) || 0)}
                  disabled={loading}
                  min="0"
                  max="100"
                  className="flex-1 px-4 py-2 border border-[#471b001e] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF3333] focus:border-transparent font-dm text-sm disabled:opacity-50"
                />
                <span className="text-sm font-medium text-[#471b00] font-dm w-16">
                  {providerPercent}%
                </span>
              </div>
              <p className="text-xs text-[#471b007f] mt-1 font-dm">
                Provider receives: {providerAmount.toFixed(4)} MATIC
              </p>
            </div>

            {/* Total Validation */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex justify-between items-center text-sm font-dm">
                <span className="text-[#471b007f]">Total:</span>
                <span
                  className={`font-semibold ${
                    clientPercent + providerPercent === 100
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}
                >
                  {clientPercent + providerPercent}%
                  {clientPercent + providerPercent === 100 ? ' ✓' : ' (must be 100%)'}
                </span>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 font-dm">
              {error}
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-[#471b001e] text-[#471b00] rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-dm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || clientPercent + providerPercent !== 100}
              className="px-4 py-2 bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm font-dm"
            >
              {loading ? 'Resolving...' : 'Resolve Dispute'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DisputeResolutionModal;
